// src/components/tokenPage/derivatives/FundingSummaryCard.tsx

import React, { useState } from "react";
import TimeRangePicker, { type TimeRangeOption } from "@/components/common/TimeRangePicker";
import { MetricBox } from "../shared/TokenDashboardComponents";
import { useTokenDerivatives } from "@/contexts/token";
import { DataCard } from "@/components/ui";

interface Props {
  ticker: string;
}

type WindowKey = '7d' | '30d' | '180d' | '365d';

const FundingSummaryCard: React.FC<Props> = () => {
  const ratiosRangeOptions: readonly TimeRangeOption[] = ['7d','30d','180d','365d'];
  const [ratiosRange, setRatiosRange] = useState<TimeRangeOption>('7d');
  
  const { fundingOiWeighted, fundingVolWeighted, isLoading } = useTokenDerivatives();

  // Get pre-computed stats for selected window
  const windowKey = ratiosRange as WindowKey;
  const oiwStats = fundingOiWeighted.stats[windowKey];
  const volwStats = fundingVolWeighted.stats[windowKey];

  // Hide if no data available (after loading)
  const hasData = fundingOiWeighted.latest != null || fundingVolWeighted.latest != null || 
                  oiwStats.cumulative != null || volwStats.cumulative != null;
  if (!isLoading && !hasData) {
    return null;
  }

  return (
    <DataCard
      title="Funding Rate"
      subtitle={`Latest, cumulative and volatility over ${ratiosRange}`}
      icon="percent"
      iconColor="text-cyan-400"
      isLoading={isLoading}
      loadingVariant="stats"
      actions={
        <TimeRangePicker options={ratiosRangeOptions} value={ratiosRange} onChange={setRatiosRange} theme="blue" />
      }
    >
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        <MetricBox label="Funding Rate (OI‑w) last" value={fundingOiWeighted.latest} fmt="ratio4" suffix="%" toneBySign />
        <MetricBox label="Funding Rate (Vol‑w) last" value={fundingVolWeighted.latest} fmt="ratio4" suffix="%" toneBySign />
        <MetricBox label={`Cumulative Funding Rate (OI‑w, ${ratiosRange})`} value={oiwStats.cumulative} fmt="ratio4" suffix="%" toneBySign />
        <MetricBox label={`Cumulative Funding Rate (Vol‑w, ${ratiosRange})`} value={volwStats.cumulative} fmt="ratio4" suffix="%" toneBySign />
        <MetricBox label={`Funding Rate Volatility (OI‑w, ${ratiosRange})`} value={oiwStats.volatility} fmt="pct2" />
        <MetricBox label={`Funding Rate Volatility (Vol‑w, ${ratiosRange})`} value={volwStats.volatility} fmt="pct2" />
      </div>
    </DataCard>
  );
};

export default FundingSummaryCard;
